<template>
    <!-- Only wrap in tooltip container if there's actual tooltip content -->
    <div
        v-if="hasTooltipContentSlot"
        :class="['tooltip-trigger', { 'tooltip-trigger--full': fullWidth }]"
        :style="{ cursor: 'pointer' }"
        @mouseenter="showTooltip"
        @mouseleave="hideTooltip"
        @focusin="showTooltip"
        @focusout="hideTooltip"
        ref="trigger"
        :aria-describedby="isVisible ? tooltipId : undefined"
    >
        <!-- Trigger slot -->
        <slot name="tooltip-trigger"></slot>

        <!-- Content slot: teleport to body so tooltip isn't affected by overflow/scroll/sticky in tables -->
        <Teleport to="body">
            <div
                :id="tooltipId"
                ref="tooltip"
                role="tooltip"
                :class="['tooltip-text', { below: isBelow }]"
                :style="tooltipStyle"
                :aria-hidden="!isVisible"
            >
                <slot name="tooltip-content"></slot>
            </div>
        </Teleport>
    </div>

    <!-- If no tooltip content, render trigger directly without wrapper -->
    <slot v-else name="tooltip-trigger"></slot>
</template>

<script>
// Constants for tooltip positioning and sizing
const VIEWPORT_PADDING = 16; // Padding on left and right of tooltip from viewport edge
const DEFAULT_MAX_WIDTH_REM = 8.9375; // Default maximum width in rem (143px at 16px base)
const ARROW_SIZE = 6; // Size of the tooltip arrow in pixels
const DEFAULT_TOOLTIP_OFFSET = 10; // Default distance between tooltip and trigger in pixels
const MIN_TOP_SPACING = 8; // Minimum spacing from top of viewport

let tooltipCounter = 0;

export default {
    name: 'ToolTip',
    props: {
        fullWidth: {
            type: Boolean,
            default: false,
        },
        position: {
            type: String,
            default: 'auto',
            validator: (value) => ['auto', 'top', 'bottom'].includes(value),
        },
        align: {
            type: String,
            default: 'center',
            validator: (value) => ['start', 'center', 'end'].includes(value),
        },
        offset: {
            type: Number,
            default: DEFAULT_TOOLTIP_OFFSET,
        },
        maxWidth: {
            type: Number,
            default: DEFAULT_MAX_WIDTH_REM,
        },
    },
    computed: {
        /** Vue 3 exposes kebab slot names on $slots as camelCase (e.g. tooltipContent). */
        hasTooltipContentSlot() {
            return Boolean(
                this.$slots['tooltipContent'] || this.$slots['tooltip-content']
            );
        },
    },
    data() {
        return {
            isBelow: false,
            isVisible: false,
            tooltipId: `tooltip-${++tooltipCounter}`,
            tooltipStyle: {
                position: 'fixed',
                visibility: 'hidden',
                opacity: 0,
                top: '0px',
                left: '0px',
                zIndex: 'var(--z-index-tooltip, 1055)',
            },
        };
    },
    methods: {
        showTooltip() {
            if (!this.$refs.trigger || !this.$refs.tooltip) return;

            this.isVisible = true;
            this.tooltipStyle = {
                ...this.tooltipStyle,
                visibility: 'hidden',
                opacity: 0,
                display: 'flex',
            };

            this.$nextTick(() => {
                const { triggerRect, viewportWidth } =
                    this.calculateTriggerBounds();
                const dynamicMaxWidth = this.calculateDynamicMaxWidth(
                    triggerRect,
                    viewportWidth
                );

                // Apply the dynamic max-width
                this.tooltipStyle = {
                    ...this.tooltipStyle,
                    maxWidth: `${dynamicMaxWidth}rem`,
                };

                // Wait for reflow with new max-width
                this.$nextTick(() => {
                    this.positionTooltip(
                        triggerRect,
                        viewportWidth,
                        dynamicMaxWidth
                    );
                });
            });
        },

        hideTooltip() {
            this.isVisible = false;
            this.tooltipStyle = {
                ...this.tooltipStyle,
                visibility: 'hidden',
                opacity: 0,
            };
            this.isBelow = false;
        },

        calculateTriggerBounds() {
            const anchors = this.$refs.trigger.querySelectorAll(
                '[data-tooltip-anchor]'
            );
            const anchor = anchors.length ? anchors[anchors.length - 1] : null;
            const anchorElement = anchor ? anchor : this.$refs.trigger;
            let triggerRect = anchorElement.getBoundingClientRect();

            if (anchorElement?.dataset?.tooltipAnchor === 'group') {
                triggerRect = this.calculateGroupBounds(anchorElement);
            }

            return {
                triggerRect,
                viewportWidth: window.innerWidth,
            };
        },

        calculateGroupBounds(anchorElement) {
            const items = anchorElement.querySelectorAll(
                '[data-tooltip-anchor-item]'
            );

            let left = Infinity;
            let top = Infinity;
            let right = -Infinity;
            let bottom = -Infinity;

            items.forEach((item) => {
                const rect = item.getBoundingClientRect();
                if (rect.width === 0 && rect.height === 0) return;
                left = Math.min(left, rect.left);
                top = Math.min(top, rect.top);
                right = Math.max(right, rect.right);
                bottom = Math.max(bottom, rect.bottom);
            });

            if (left !== Infinity) {
                return {
                    left,
                    top,
                    right,
                    bottom,
                    width: right - left,
                    height: bottom - top,
                };
            }

            return anchorElement.getBoundingClientRect();
        },

        calculateDynamicMaxWidth(triggerRect, viewportWidth) {
            // Calculate available space from center of triggerRect
            const triggerCenterX = triggerRect.left + triggerRect.width / 2;

            // Calculate available space on left and right from triggerRect center
            const availableLeft = triggerCenterX - VIEWPORT_PADDING;
            const availableRight =
                viewportWidth - triggerCenterX - VIEWPORT_PADDING;

            const totalAvailableWidth = Math.min(
                viewportWidth - VIEWPORT_PADDING * 2,
                availableLeft + availableRight
            );

            // Convert to rem (assuming 16px = 1rem)
            const maxAvailableWidthRem = totalAvailableWidth / 16;

            // Use the smaller of prop max-width or available width
            return Math.min(this.maxWidth, maxAvailableWidthRem);
        },

        positionTooltip(triggerRect, viewportWidth, dynamicMaxWidth) {
            const tooltipRect = this.$refs.tooltip.getBoundingClientRect();
            const triggerCenterX = triggerRect.left + triggerRect.width / 2;

            // Calculate top position
            const top = triggerRect.top - tooltipRect.height - this.offset;

            // Calculate left position based on align prop
            let left;
            if (this.align === 'start') {
                left = triggerRect.left;
            } else if (this.align === 'end') {
                left = triggerRect.right - tooltipRect.width;
            } else {
                // 'center' (default)
                left = triggerCenterX - tooltipRect.width / 2;
            }

            // Check if tooltip fits within viewport bounds
            const adjustedLeft = Math.max(
                VIEWPORT_PADDING,
                Math.min(
                    left,
                    viewportWidth - tooltipRect.width - VIEWPORT_PADDING
                )
            );

            // Determine if tooltip should show below based on position prop or available space
            let showBelow = this.isBelow;
            if (this.position === 'bottom') {
                showBelow = true;
            } else if (this.position === 'top') {
                showBelow = false;
            } else {
                // Auto mode: show below if it doesn't fit above
                showBelow = top < MIN_TOP_SPACING;
            }

            const finalTop = showBelow
                ? triggerRect.bottom + this.offset
                : Math.max(MIN_TOP_SPACING, top);
            this.isBelow = showBelow;

            this.tooltipStyle = {
                position: 'fixed',
                visibility: 'visible',
                opacity: 1,
                top: `${finalTop}px`,
                left: `${adjustedLeft}px`,
                zIndex: 2000,
                display: 'flex',
                maxWidth: `${dynamicMaxWidth}rem`,
                '--arrow-left': '50%',
            };
        },
    },
};
</script>

<style scoped>
.tooltip-trigger {
    position: relative;
    display: inline-block;
}

.tooltip-trigger--full {
    display: block;
    width: 100%;
}

.tooltip-text {
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: var(--black);
    color: white;
    padding: var(--space-2) var(--space-3);
    border-radius: var(--radius-sm);
    font-size: 0.875rem;
    line-height: 1.35;
    font-weight: 400;
    font-family: Inter, sans-serif;
    min-width: 4rem;
    max-width: 8.9375rem;
    word-wrap: break-word;
    overflow-wrap: break-word;
    hyphens: auto;
    white-space: normal;
    text-align: center;
    transition: opacity 0.2s;
    pointer-events: none;
}

/* Arrow pointing down */
.tooltip-text::after {
    content: '';
    position: absolute;
    top: 100%;
    left: var(--arrow-left, 50%);
    margin-left: -6px;
    border-width: 6px;
    border-style: solid;
    border-color: var(--black) transparent transparent transparent;
}

/* Arrow pointing up */
.tooltip-text.below::after {
    top: -12px;
    left: var(--arrow-left, 50%);
    border-color: transparent transparent var(--black) transparent;
}
</style>
